// ─── Chat Hook ────────────────────────────────────────────────────────────────
// Manages chat messages, polling, and sending for a given conversation.
// Usage: const chat = useChat({ conversationId, webhookUrl, pollUrl, agentName });

import { useState, useEffect, useRef, useCallback } from 'react';
import { ChatMessage } from '../lib/types';
import { apiGetChatHistory, apiAddChatMessage, apiChatSend, apiChatPoll } from '../lib/api';

interface UseChatOptions {
  conversationId: string;
  webhookUrl?: string;
  pollUrl?: string;
  agentName?: string;
}

export function useChat({ conversationId, webhookUrl, pollUrl, agentName }: UseChatOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastMessageIdRef = useRef<string | undefined>(undefined);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load history on mount / conversationId change
  useEffect(() => {
    setLoading(true);
    apiGetChatHistory(conversationId)
      .then(({ messages: msgs }) => {
        setMessages(msgs);
        if (msgs.length > 0) {
          lastMessageIdRef.current = msgs[msgs.length - 1].id;
        }
      })
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  }, [conversationId]);

  // Polling for new messages
  useEffect(() => {
    if (!pollUrl) return;

    const poll = async () => {
      try {
        const { messages: newMsgs } = await apiChatPoll(pollUrl, lastMessageIdRef.current);
        if (newMsgs && newMsgs.length > 0) {
          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => m.id));
            const fresh = newMsgs.filter((m) => !existingIds.has(m.id));
            if (fresh.length === 0) return prev;
            fresh.forEach((m) => {
              apiAddChatMessage(conversationId, m).catch(() => {});
            });
            lastMessageIdRef.current = fresh[fresh.length - 1].id;
            return [...prev, ...fresh];
          });
        }
      } catch {}
    };

    pollIntervalRef.current = setInterval(poll, 3000);
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [pollUrl, conversationId]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return;
      const msg: ChatMessage = {
        id: `user-${Date.now()}`,
        sender: 'user',
        content: content.trim(),
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, msg]);
      await apiAddChatMessage(conversationId, msg).catch(() => {});

      if (webhookUrl) {
        setSending(true);
        try {
          await apiChatSend(webhookUrl, agentName || 'KAELO', content.trim());
        } catch {}
        setSending(false);
      }
    },
    [conversationId, webhookUrl, agentName]
  );

  const clearHistory = useCallback(async () => {
    setMessages([]);
    await import('../lib/api').then(({ apiClearChatHistory }) =>
      apiClearChatHistory(conversationId).catch(() => {})
    );
  }, [conversationId]);

  return { messages, loading, sending, sendMessage, clearHistory };
}
